// 보상기준 마스터 왼쪽 ① 고객사 목록(구성안 화면⑥) — 머리 '고객사 목록 N개사' · 검색(이름·센터) · 품온 거르기
// · 미등록 경고(누르면 미등록만) · 맨 위 '공통 기준' 줄 · 표(고객사 | 센터 | 품온 배지) · [+ 고객사 기준 신규 등록] 폼.
// 서버 GET /api/claims/shippers(routers/claims.py · services/claim_master.load_shippers) — 이름·센터·품온만 온다.
// 🔴 품온 정본은 고객사 기준(standard.temp_zone)이다 — 명부 글자에서 온 temp_zone_hint 는 등록 폼의 기본값으로만 쓴다.
// 🔴 등록 단추·폼은 canEdit 일 때만 그린다(보기 전용이면 목록만). 지우는 단추는 없다(서버에 DELETE 경로가 없다).
// 🔴 고르기 = 행을 누른다(안의 단추가 aria-pressed 를 든다 — <tr> 에는 aria-pressed 를 둘 수 없다) · 공통 기준 = onSelect(null).
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui';
import { claimsAPI } from '../../api/client';
import { toast } from '../../components/AppDialog';
import { COLORS, badge, input } from '../../utils/uiStyles';
import { errorText, textOrNull } from './claimLabels';

// 거르기 select — 전체 · 냉장/냉동 · 상온 · 미등록(품온은 등록된 고객사 기준의 품온).
export const SHIPPER_FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'cold', label: '냉장/냉동' },
  { key: 'ambient', label: '상온' },
  { key: 'unregistered', label: '미등록' },
];
// 품온 배지 — 냉장·냉동 파랑 · 상온 회색 · 미등록 빨강.
const ZONE_BADGES = { cold: { tone: 'blue', label: '냉장/냉동' }, ambient: { tone: 'gray', label: '상온' } };
export const SHIPPER_ZONES = [
  { key: 'cold', label: '냉장/냉동' },
  { key: 'ambient', label: '상온' },
];
export const zoneOfShipper = (item) => (item && item.standard ? item.standard.temp_zone : null);

const th = { padding: '6px 6px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const td = { padding: '6px 6px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle' };
const rowBtn = { border: 0, background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' };
const fieldBox = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub };
const errText = { color: COLORS.dangerText, fontSize: '12px' };

export function ZoneBadge({ zone }) {
  const z = ZONE_BADGES[zone];
  if (!z) return <span data-testid="claim-shipper-zone" style={badge('danger')}>미등록</span>;
  return <span data-testid="claim-shipper-zone" style={badge(z.tone)}>{z.label}</span>;
}

/**
 * 고객사 기준 등록 폼 — shippers = 고를 수 있는 미등록 고객사 · fixed = 고른 고객사 하나(배너에서 연다 · select 대신 이름).
 * 품온 라디오 기본값 = 그 고객사 temp_zone_hint(없으면 비움 — 골라야 한다) · 공문 번호 · 변경 사유 → createShipperStandard.
 * 성공하면 onDone(고객사 id) 를 기다린다(부르는 쪽이 다시 읽는다).
 */
export function ShipperStandardForm({ shippers, fixed, onDone, onCancel }) {
  const options = useMemo(() => (fixed ? [fixed] : (shippers || [])), [fixed, shippers]);
  const [shipperId, setShipperId] = useState(fixed ? String(fixed.id) : '');
  const [zone, setZone] = useState(fixed && fixed.temp_zone_hint ? fixed.temp_zone_hint : '');
  const [docRef, setDocRef] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const picked = options.find((s) => String(s.id) === shipperId) || null;
  const radioName = `claim-shipper-zone-${fixed ? fixed.id : 'new'}`;

  const onPick = (value) => {
    setShipperId(value);
    const s = options.find((x) => String(x.id) === value);
    setZone(s && s.temp_zone_hint ? s.temp_zone_hint : '');
    setError('');
  };

  const onSave = async () => {
    if (!picked) { setError('등록할 고객사를 고르세요.'); return; }
    if (!zone) { setError('품온을 고르세요.'); return; }
    const body = { shipper_id: picked.id, temp_zone: zone };
    const doc = textOrNull(docRef);
    if (doc) body.doc_ref = doc;
    const why = textOrNull(reason);
    if (why) body.reason = why;
    setBusy(true);
    setError('');
    try {
      await claimsAPI.createShipperStandard(body);
    } catch (e) {
      const msg = errorText(e, '고객사 기준을 등록하지 못했습니다');
      setError(msg);
      toast(msg, 'error');
      setBusy(false);
      return;
    }
    setBusy(false);
    toast('고객사 기준을 등록했습니다');
    if (onDone) await onDone(picked.id);
  };

  return (
    <div role="group" aria-label="고객사 기준 등록" data-testid="claim-shipper-form"
      style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', background: COLORS.surface }}>
      {fixed ? (
        <div style={{ fontSize: '13px', fontWeight: 600, color: COLORS.text }}>{fixed.display_name}{fixed.center_name ? ` · ${fixed.center_name}` : ''}</div>
      ) : (
        <label style={fieldBox}>
          <span>고객사(기준 미등록)</span>
          <select value={shipperId} onChange={(e) => onPick(e.target.value)} style={input}>
            <option value="">— 고르세요 —</option>
            {options.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.display_name}{s.center_name ? ` · ${s.center_name}` : ''}</option>
            ))}
          </select>
        </label>
      )}
      <div role="radiogroup" aria-label="고객사 품온" style={{ display: 'flex', gap: '12px', fontSize: '13px', color: COLORS.text }}>
        {SHIPPER_ZONES.map((z) => (
          <label key={z.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <input type="radio" name={radioName} value={z.key} checked={zone === z.key} onChange={() => setZone(z.key)} />
            {z.label}
          </label>
        ))}
      </div>
      <label style={fieldBox}>
        <span>기준 공문 번호(선택)</span>
        <input type="text" value={docRef} maxLength={100} onChange={(e) => setDocRef(e.target.value)} style={input} />
      </label>
      <label style={fieldBox}>
        <span>변경 사유(선택)</span>
        <input type="text" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} style={input} />
      </label>
      {error && <div role="alert" style={errText}>{error}</div>}
      <div style={{ display: 'flex', gap: '6px' }}>
        <Button size="sm" onClick={onSave} disabled={busy}>{busy ? '등록 중…' : '등록'}</Button>
        {onCancel && <Button variant="secondary" size="sm" onClick={onCancel}>닫기</Button>}
      </div>
    </div>
  );
}

export default function ClaimShipperPanel({ canEdit, selectedId, onSelect, reloadKey, onChanged }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);        // 이 패널에서 등록한 뒤 다시 읽는다
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    // 모킹이 비었거나 함수가 없어도 동기 예외가 아니라 오류 상태가 되게 then 안에서 부른다.
    Promise.resolve().then(() => claimsAPI.shippers()).then((r) => {
      if (!alive) return;
      setData((r && r.data) || {});
      setError('');
      setState('ready');
    }).catch((e) => {
      if (!alive) return;
      setError(errorText(e, '고객사 목록을 불러오지 못했습니다'));
      setState('error');
    });
    return () => { alive = false; };
  }, [reloadKey, version]);

  const items = useMemo(() => (data && Array.isArray(data.items) ? data.items : []), [data]);
  const total = data && Number.isFinite(data.total) ? data.total : items.length;
  const unregistered = data && Number.isFinite(data.unregistered_count)
    ? data.unregistered_count
    : items.filter((it) => !it.standard).length;
  const unregisteredItems = useMemo(() => items.filter((it) => !it.standard), [items]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !`${it.display_name || ''} ${it.center_name || ''}`.toLowerCase().includes(q)) return false;
      if (filter === 'unregistered') return !it.standard;
      if (filter === 'cold' || filter === 'ambient') return zoneOfShipper(it) === filter;
      return true;
    });
  }, [items, query, filter]);

  const pick = (id) => { if (onSelect) onSelect(id); };
  const afterRegister = async (id) => {
    setFormOpen(false);
    setVersion((v) => v + 1);
    if (onChanged) await onChanged(id);
  };
  const common = selectedId === null || selectedId === undefined;

  return (
    <aside aria-label="고객사 목록" data-testid="claim-shipper-panel"
      style={{ width: '300px', flexShrink: 0, borderRight: `1px solid ${COLORS.borderSoft}`, padding: '16px 12px', overflowY: 'auto', background: COLORS.surface, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <h2 data-testid="claim-shipper-count" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
        {state === 'ready' ? `고객사 목록 ${total}개사` : '고객사 목록'}
      </h2>
      {state === 'loading' && <div role="status" style={{ fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div>}
      {state === 'error' && (
        <div role="alert" style={{ ...errText, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {error}
          <Button variant="secondary" size="sm" onClick={() => setVersion((v) => v + 1)}>다시 시도</Button>
        </div>
      )}
      {state === 'ready' && (
        <>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input type="search" aria-label="고객사명 검색" placeholder="고객사명 검색" value={query}
              onChange={(e) => setQuery(e.target.value)} style={{ ...input, flex: 1, minWidth: 0 }} />
            <select aria-label="품온 거르기" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...input, width: '96px' }}>
              {SHIPPER_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          {unregistered > 0 && (
            <button type="button" data-testid="claim-shipper-unregistered" aria-pressed={filter === 'unregistered'}
              onClick={() => setFilter('unregistered')}
              style={{ ...rowBtn, padding: '6px 8px', borderRadius: '6px', background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '12px', fontWeight: 600 }}>
              ⚠ 기준 미등록 고객사 {unregistered}곳
            </button>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr><th style={th}>고객사</th><th style={th}>센터</th><th style={th}>품온</th></tr>
            </thead>
            <tbody>
              <tr data-testid="claim-shipper-common" aria-selected={common} onClick={() => pick(null)}
                style={{ cursor: 'pointer', background: common ? COLORS.primarySoft : undefined }}>
                <td colSpan={3} style={{ ...td, fontWeight: common ? 600 : 500 }}>
                  <button type="button" aria-pressed={common} style={rowBtn}>공통 기준</button>
                  <span style={{ marginLeft: '6px', fontSize: '11px', color: COLORS.textSub }}>모든 고객사 기본값</span>
                </td>
              </tr>
              {shown.length === 0 && (
                <tr><td colSpan={3} style={{ ...td, color: COLORS.textMuted }}>맞는 고객사가 없습니다.</td></tr>
              )}
              {shown.map((it) => {
                const on = selectedId === it.id;
                return (
                  <tr key={it.id} data-testid="claim-shipper-row" aria-selected={on} onClick={() => pick(it.id)}
                    style={{ cursor: 'pointer', background: on ? COLORS.primarySoft : undefined }}>
                    <td style={{ ...td, fontWeight: on ? 600 : 400 }}>
                      <button type="button" aria-pressed={on} style={rowBtn}>{it.display_name}</button>
                    </td>
                    <td style={{ ...td, color: COLORS.textSub, fontSize: '12px' }}>{it.center_name || it.center_key || '—'}</td>
                    <td style={td}><ZoneBadge zone={zoneOfShipper(it)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {canEdit && !formOpen && (
            <Button variant="secondary" size="sm" onClick={() => setFormOpen(true)}>+ 고객사 기준 신규 등록</Button>
          )}
          {canEdit && formOpen && (
            <ShipperStandardForm shippers={unregisteredItems} onDone={afterRegister} onCancel={() => setFormOpen(false)} />
          )}
        </>
      )}
    </aside>
  );
}
